import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { type ApiError, api, qs, unwrap } from '../api/client';
import { useAccounts, useAutomations } from '../api/hooks';
import type {
  Account,
  Automation,
  AutomationStats,
  Flow,
  Items,
  Matcher,
  Media,
  MessagePayload,
} from '../api/types';
import { useSession } from '../auth/session';
import {
  Badge,
  Card,
  DataTable,
  Empty,
  ErrorAlert,
  Field,
  fmtDate,
  Modal,
  PageHeader,
  relTime,
  useToast,
} from '../components/ui';

export const VARIABLES = [
  '{{first_name}}',
  '{{username}}',
  '{{comment_text}}',
  '{{post_name}}',
  '{{account_name}}',
  '{{tracking_link}}',
  '{{current_date}}',
];
type AutomationType =
  | 'comment'
  | 'all_comments'
  | 'keyword'
  | 'dm'
  | 'story'
  | 'story_mention'
  | 'post_share'
  | 'flow';

interface Draft {
  accountId: string;
  mediaIds: string[];
  anyMedia: boolean;
  type: AutomationType;
  name: string;
  matcherKind: Matcher['kind'];
  keywords: string;
  commentReplies: string[];
  nonFollowerReplies: string[];
  welcomeMessages: string[];
  aiCommentReply: boolean;
  dmMessages: MessagePayload[];
  delaySeconds: number;
  handoffMessage: string;
  flowId: string;
  /** UI-25: the trigger of the chosen flow; it becomes the automation's trigger. */
  flowTrigger: FlowTrigger;
  /** AUT-10 schedule window as `datetime-local` values; empty = always on. */
  activeFrom: string;
  activeUntil: string;
}

/** UI-66: a draft preset to an account, every post, for the post dialog's "New automation…". */
export const draftForAccount = (accountId: string): Draft => ({
  ...empty,
  accountId,
  anyMedia: true,
});

const empty: Draft = {
  accountId: '',
  mediaIds: [],
  anyMedia: false,
  type: 'keyword',
  name: '',
  matcherKind: 'contains',
  keywords: '',
  commentReplies: ['Check your DMs {{username}}!'],
  nonFollowerReplies: [],
  welcomeMessages: [],
  aiCommentReply: false,
  dmMessages: [{ kind: 'text', text: 'Hi {{first_name}}, here is what you asked for.' }],
  delaySeconds: 0,
  handoffMessage: '',
  flowId: '',
  flowTrigger: 'comment',
  activeFrom: '',
  activeUntil: '',
};

export type FlowTrigger = 'comment' | 'story_reply' | 'dm' | 'story_mention' | 'post_share';
/** UI-44: every trigger a flow can declare and an automation can carry. */
const isFlowTrigger = (on: unknown): on is Exclude<FlowTrigger, 'comment'> =>
  on === 'story_reply' || on === 'dm' || on === 'story_mention' || on === 'post_share';

/** UI-25: the trigger a flow starts on, read from its trigger node (comment when absent). */
export function flowTriggerOf(f: {
  definition: { nodes: ReadonlyArray<{ type: string; on?: string } & Record<string, unknown>> };
}): FlowTrigger {
  const on = f.definition.nodes.find((n) => n.type === 'trigger')?.on;
  return isFlowTrigger(on) ? on : 'comment';
}

/** UI-32: the list's Trigger column: what the automation fires on, plus the keyword rule if any. */
export function triggerLabel(a: Pick<Automation, 'triggerType' | 'matcher'>): string {
  const rule =
    a.matcher.kind === 'any'
      ? null
      : a.matcher.kind === 'exact'
        ? `exact: ${a.matcher.keyword}`
        : a.matcher.kind === 'contains'
          ? `keyword: ${a.matcher.keyword}`
          : `${a.matcher.kind === 'any_of' ? 'any of' : 'all of'}: ${a.matcher.keywords.join(', ')}`;
  const capital = (t: string) => t.charAt(0).toUpperCase() + t.slice(1);
  switch (a.triggerType) {
    case 'story_reply':
      return rule ? `Story reply ${rule}` : 'Any story reply';
    case 'dm':
      return rule ? `DM ${rule}` : 'Any DM';
    case 'story_mention':
      return 'Story mention';
    case 'post_share':
      return 'Post shared to DM';
    default:
      return rule ? capital(rule) : 'Any comment';
  }
}

/** UI-28: the list's Type column. */
export function typeLabel(triggerType: string): string {
  const labels: Record<string, string> = {
    comment: 'Post',
    story_reply: 'Story',
    dm: 'DM',
    story_mention: 'Story mention',
    post_share: 'Post share',
  };
  return labels[triggerType] ?? triggerType;
}

/** UI-64: a comment automation's Type names what it is bound to: Post, Reel, or both. */
export function mediaTypeLabel(
  a: Pick<Automation, 'triggerType' | 'mediaBinding'>,
  kindOf: (externalId: string) => string | undefined,
): string {
  if (a.triggerType !== 'comment' || a.mediaBinding.kind !== 'media')
    return typeLabel(a.triggerType);
  const kinds = new Set(a.mediaBinding.mediaIds.map((id) => kindOf(id) ?? 'post'));
  if (kinds.size === 1 && kinds.has('reel')) return 'Reel';
  return kinds.has('reel') ? 'Posts and reels' : 'Post';
}

/** UI-64: the caption of a bound media item, trimmed; the id when nothing is known. */
export const mediaLabel = (caption: string | null | undefined, externalId: string): string =>
  caption && caption.trim() !== ''
    ? caption.length > 40
      ? `${caption.slice(0, 40)}…`
      : caption
    : externalId;

/** Types that fire on any text: no keyword matcher (UI-44: story replies do take one). */
const ANY_MATCH_TYPES: readonly AutomationType[] = ['story_mention', 'post_share'];

/** The trigger a draft submits: flows carry their own, every other type is fixed. */
const triggerOf = (d: Draft): string =>
  d.type === 'flow'
    ? d.flowTrigger
    : d.type === 'story'
      ? 'story_reply'
      : d.type === 'story_mention' || d.type === 'post_share' || d.type === 'dm'
        ? d.type
        : 'comment';
/** Drafts that bind media and reply publicly: comment-triggered ones only. */
const aboutPosts = (d: Draft): boolean => triggerOf(d) === 'comment';
/** Drafts whose media picker lists stories. */
const aboutStories = (d: Draft): boolean => triggerOf(d) === 'story_reply';
/** AUT-16/UI-44: only post and story triggers have media of ours to bind. */
const bindsMedia = (d: Draft): boolean => aboutPosts(d) || aboutStories(d);

/** Pure: what stops a draft from being submitted, or null. Exactness cannot be spread over several words. */
/** UI-43: the matcher a card starts with; "Any comment" wherever a keyword is optional. UI-43 (revised 2): the Comment to DM card is exact. */
export function cardDefaultMatcher(type: Draft['type']): Matcher['kind'] {
  if (type === 'comment') return 'exact';
  return type === 'keyword' || type === 'dm' ? 'contains' : 'any';
}

/** UI-43: the cards whose matcher can need a keyword. */
const keywordCard = (d: Draft): boolean =>
  d.type === 'comment' ||
  d.type === 'story' ||
  d.type === 'keyword' ||
  d.type === 'dm' ||
  (d.type === 'flow' && !anyMatchFlow(d));
/** UI-44 (completed): mentions and shares fire on any text; comment, DM and story-reply flows take a keyword. */
const anyMatchFlow = (d: Draft): boolean =>
  d.flowTrigger === 'story_mention' || d.flowTrigger === 'post_share';
/** UI-44 (completed 2): the "any" option named after the draft's trigger. */
export function anyLabel(d: Draft): string {
  const t = triggerOf(d);
  return t === 'dm' ? 'Any DM' : t === 'story_reply' ? 'Any story reply' : 'Any comment';
}
/** UI-44 (completed): the Scope select only for a plain draft that binds media; never for a flow. */
const showScope = (d: Draft): boolean => d.type !== 'flow' && bindsMedia(d);

export function draftProblem(d: Draft): string | null {
  const kws = d.keywords
    .split(',')
    .map((k) => k.trim())
    .filter(Boolean);
  // UI-42/UI-43: a trigger that matches a keyword needs one; post and story triggers need media
  if (keywordCard(d) && d.matcherKind !== 'any' && kws.length === 0)
    return 'Add at least one keyword for this trigger.';
  if (aboutPosts(d) && !d.anyMedia && d.mediaIds.length === 0)
    return 'Pick a post or reel, or apply to every post and reel.';
  if (aboutStories(d) && !d.anyMedia && d.mediaIds.length === 0)
    return 'Pick a story, or apply to every story.';
  return d.type === 'keyword' && d.matcherKind === 'exact' && kws.length > 1
    ? 'Exact match takes one keyword. Use "Contains any word" to match several.'
    : null;
}

/** Pure: the wizard draft to the API body. */
export function toBody(d: Draft) {
  const kws = d.keywords
    .split(',')
    .map((k) => k.trim())
    .filter(Boolean);
  const matcher: Matcher =
    d.type === 'all_comments' ||
    ANY_MATCH_TYPES.includes(d.type) ||
    (d.type === 'flow' && anyMatchFlow(d))
      ? { kind: 'any' }
      : d.matcherKind === 'any'
        ? { kind: 'any' }
        : d.matcherKind === 'contains' && kws.length > 1
          ? { kind: 'any_of', keywords: kws }
          : d.matcherKind === 'exact' || d.matcherKind === 'contains'
            ? { kind: d.matcherKind, keyword: kws[0] ?? '' }
            : { kind: d.matcherKind, keywords: kws };
  return {
    accountId: d.accountId,
    name: d.name,
    triggerType: triggerOf(d),
    // AUT-16/UI-44: only post and story triggers have media of ours to bind
    mediaBinding:
      d.anyMedia || !bindsMedia(d) ? { kind: 'any' } : { kind: 'media', mediaIds: d.mediaIds },
    matcher,
    commentReplies: aboutPosts(d) ? d.commentReplies.filter((r) => r.trim()) : [],
    nonFollowerReplies: d.nonFollowerReplies.filter((r) => r.trim()),
    welcomeMessages: d.welcomeMessages.filter((r) => r.trim()),
    dmMessages: d.type === 'flow' ? [] : d.dmMessages,
    delaySeconds: d.delaySeconds,
    aiCommentReply: d.aiCommentReply,
    handoffMessage: d.handoffMessage || null,
    flowId: d.type === 'flow' && d.flowId ? d.flowId : null,
    activeFrom: d.activeFrom ? new Date(d.activeFrom).toISOString() : null,
    activeUntil: d.activeUntil ? new Date(d.activeUntil).toISOString() : null,
  };
}

/** An ISO instant as the local `datetime-local` value (minutes), or '' for null. */
export function toLocalInput(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** Pure: an existing automation back into a wizard draft. */
export function toDraft(a: Automation): Draft {
  const kws =
    a.matcher.kind === 'exact' || a.matcher.kind === 'contains'
      ? a.matcher.keyword
      : a.matcher.kind === 'any_of' || a.matcher.kind === 'all_of'
        ? a.matcher.keywords.join(', ')
        : '';
  return {
    accountId: a.accountId,
    mediaIds: a.mediaBinding.kind === 'media' ? a.mediaBinding.mediaIds : [],
    anyMedia: a.mediaBinding.kind === 'any',
    type: a.flowId
      ? 'flow'
      : a.triggerType === 'story_mention' ||
          a.triggerType === 'post_share' ||
          a.triggerType === 'dm'
        ? a.triggerType
        : a.triggerType === 'story_reply'
          ? 'story'
          : a.matcher.kind === 'any'
            ? 'all_comments'
            : a.matcher.kind === 'exact'
              ? 'comment' // UI-43 (revised 2)
              : 'keyword',
    name: a.name,
    flowTrigger: isFlowTrigger(a.triggerType) ? a.triggerType : 'comment',
    matcherKind: a.matcher.kind,
    keywords: kws,
    commentReplies: a.commentReplies,
    nonFollowerReplies: a.nonFollowerReplies,
    welcomeMessages: a.welcomeMessages,
    aiCommentReply: a.aiCommentReply,
    dmMessages: a.dmMessages,
    delaySeconds: a.delaySeconds,
    handoffMessage: a.handoffMessage ?? '',
    flowId: a.flowId ?? '',
    activeFrom: toLocalInput(a.activeFrom),
    activeUntil: toLocalInput(a.activeUntil),
  };
}

function MessageEditor({
  value,
  onChange,
}: {
  value: MessagePayload;
  onChange: (m: MessagePayload) => void;
}) {
  const kind = value.kind;
  return (
    <div className="card" style={{ padding: 12 }}>
      <div className="row">
        <Field label="Format">
          <select
            className="input"
            value={kind}
            onChange={(e) => {
              const k = e.target.value as MessagePayload['kind'];
              onChange(
                k === 'text'
                  ? { kind: 'text', text: '' }
                  : k === 'button'
                    ? { kind: 'button', text: '', buttons: [{ title: 'Open', url: 'https://' }] }
                    : k === 'image' || k === 'video'
                      ? { kind: k, url: 'https://', caption: '' }
                      : k === 'file'
                        ? { kind: 'file', url: 'https://' }
                        : {
                            kind: 'ai_text',
                            instruction: 'Answer the question helpfully',
                            context: {},
                            fallbackText: 'Thanks for reaching out!',
                          },
              );
            }}
          >
            <option value="text">Text</option>
            <option value="button">Button</option>
            <option value="image">Image</option>
            <option value="video">Video</option>
            <option value="file">PDF</option>
            <option value="ai_text">AI generated</option>
          </select>
        </Field>
      </div>
      {value.kind === 'text' ? (
        <>
          <Field label="Message">
            <textarea
              className="input"
              value={value.text}
              onChange={(e) => onChange({ ...value, text: e.target.value })}
            />
          </Field>
          {/* UI-58: the same choice a button has; unticked sends the links as written (AUT-13) */}
          <label className="row" style={{ alignItems: 'center', gap: 6 }}>
            <input
              type="checkbox"
              checked={value.track !== false}
              onChange={(e) => {
                const { track: _t, ...rest } = value;
                onChange(e.target.checked ? rest : { ...rest, track: false as const });
              }}
            />
            <span className="small">Track clicks in this text</span>
          </label>
          <span className="help">
            {value.track === false ? 'Links sent as written' : 'Links tracked automatically'}
          </span>
        </>
      ) : null}
      {value.kind === 'button' ? (
        <>
          <Field label="Message">
            <textarea
              className="input"
              value={value.text}
              onChange={(e) => onChange({ ...value, text: e.target.value })}
            />
          </Field>
          {value.buttons.map((b, i) => (
            <div className="row" key={`${i}-${b.title}`}>
              <Field label="Button title">
                <input
                  className="input"
                  value={b.title}
                  onChange={(e) =>
                    onChange({
                      ...value,
                      buttons: value.buttons.map((x, j) =>
                        j === i ? { ...x, title: e.target.value } : x,
                      ),
                    })
                  }
                />
              </Field>
              <Field
                label={
                  b.track === false ? 'Link (sent as written)' : 'Link (tracked automatically)'
                }
              >
                <input
                  className="input"
                  value={b.url}
                  onChange={(e) =>
                    onChange({
                      ...value,
                      buttons: value.buttons.map((x, j) =>
                        j === i ? { ...x, url: e.target.value } : x,
                      ),
                    })
                  }
                />
              </Field>
              <label className="row" style={{ alignItems: 'center', gap: 6 }}>
                <input
                  type="checkbox"
                  checked={b.track !== false}
                  onChange={(e) =>
                    onChange({
                      ...value,
                      buttons: value.buttons.map((x, j) => {
                        if (j !== i) return x;
                        const { track: _t, ...rest } = x;
                        return e.target.checked ? rest : { ...rest, track: false as const };
                      }),
                    })
                  }
                />
                <span className="small">Track clicks</span>
              </label>
            </div>
          ))}
          {value.buttons.length < 3 ? (
            <button
              type="button"
              className="btn btn-sm"
              onClick={() =>
                onChange({ ...value, buttons: [...value.buttons, { title: '', url: 'https://' }] })
              }
            >
              Add button
            </button>
          ) : null}
        </>
      ) : null}
      {value.kind === 'image' || value.kind === 'video' ? (
        <div className="row">
          <Field
            label={value.kind === 'video' ? 'Video URL (https)' : 'Image URL (https)'}
            help={
              value.kind === 'video'
                ? 'A public https link to an mp4 or mov; Instagram applies its own size and length limits'
                : 'A public https link to a PNG or JPEG; Instagram accepts images up to 8 MB'
            }
          >
            <input
              className="input"
              value={value.url}
              onChange={(e) => onChange({ ...value, url: e.target.value })}
            />
          </Field>
          <Field label="Caption">
            <input
              className="input"
              value={value.caption ?? ''}
              onChange={(e) => onChange({ ...value, caption: e.target.value })}
            />
          </Field>
        </div>
      ) : null}
      {/* AUT-20: Instagram sends the PDF itself as a file attachment */}
      {value.kind === 'file' ? (
        <div className="row">
          <Field
            label="PDF URL (https)"
            help="A public https link ending in .pdf; Instagram attaches PDFs up to 25 MB"
          >
            <input
              className="input"
              value={value.url}
              onChange={(e) => onChange({ kind: 'file', url: e.target.value })}
            />
          </Field>
        </div>
      ) : null}
      {value.kind === 'ai_text' ? (
        <div className="row">
          <Field label="Instruction for the AI">
            <input
              className="input"
              value={value.instruction}
              onChange={(e) => onChange({ ...value, instruction: e.target.value })}
            />
          </Field>
          <Field label="Fallback text (sent if AI fails)">
            <input
              className="input"
              value={value.fallbackText}
              onChange={(e) => onChange({ ...value, fallbackText: e.target.value })}
            />
          </Field>
        </div>
      ) : null}
    </div>
  );
}

function ListEditor({
  label,
  values,
  onChange,
  help,
}: {
  label: string;
  values: string[];
  onChange: (v: string[]) => void;
  help?: string;
}) {
  return (
    <Field label={label} help={help} group>
      {values.map((v, i) => (
        <div className="row" key={`reply-${i}`}>
          <input
            className="input"
            value={v}
            onChange={(e) => onChange(values.map((x, j) => (j === i ? e.target.value : x)))}
          />
          <button
            type="button"
            className="btn btn-sm"
            onClick={() => onChange(values.filter((_, j) => j !== i))}
          >
            Remove
          </button>
        </div>
      ))}
      <button type="button" className="btn btn-sm" onClick={() => onChange([...values, ''])}>
        Add variant
      </button>
    </Field>
  );
}

const wizardTitle = (readOnly: boolean, existing: Automation | null): string =>
  readOnly && existing
    ? `View ${existing.name}`
    : existing
      ? `Edit ${existing.name}`
      : 'New automation';

/** UI-44 (completed): the post/story scope of a plain draft. */
function ScopeField({ d, setD }: { d: Draft; setD: (next: Draft) => void }) {
  return (
    <Field label="Scope">
      <select
        className="input"
        value={aboutStories(d) ? 'story' : 'post'}
        onChange={(e) =>
          setD({ ...d, type: e.target.value === 'story' ? 'story' : 'keyword', mediaIds: [] })
        }
      >
        <option value="post">Post or reel</option>
        <option value="story">Story</option>
      </select>
    </Field>
  );
}

/** UI-40/UI-41: the View's footer walks the steps and closes; it never saves. */
function ReadOnlyFooter({
  step,
  setStep,
  onClose,
}: {
  step: number;
  setStep: (s: number) => void;
  onClose: () => void;
}) {
  return (
    <>
      {step > 0 ? (
        <button type="button" className="btn" onClick={() => setStep(step - 1)}>
          Back
        </button>
      ) : null}
      {step < 2 ? (
        <button type="button" className="btn" onClick={() => setStep(step + 1)}>
          Continue
        </button>
      ) : null}
      <button type="button" className="btn" onClick={onClose}>
        Close
      </button>
    </>
  );
}

export function AutomationWizard({
  initial,
  existing,
  onClose,
  accounts,
  flows,
  readOnly = false,
  onCreated,
  hint,
}: {
  initial: Draft;
  existing: Automation | null;
  onClose: () => void;
  accounts: Account[];
  flows: Flow[];
  /** UI-40: the PRD's View action; every input disabled, nothing to save. */
  readOnly?: boolean;
  /** UI-66: the post dialog learns which automation was just created. */
  onCreated?: (a: Automation) => void;
  /** UI-66: one sentence shown above the steps (why this wizard was opened). */
  hint?: string;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const [step, setStep] = useState(existing ? 2 : 0);
  const [d, setD] = useState<Draft>({
    ...initial,
    accountId: initial.accountId || accounts[0]?.id || '',
  });
  const media = useQuery({
    queryKey: ['media', d.accountId, aboutStories(d) ? 'story' : 'feed'],
    queryFn: () =>
      unwrap(
        api.get<Items<Media>>(
          `/portal/social-accounts/${d.accountId}/media${aboutStories(d) ? qs({ kind: 'story' }) : ''}`,
        ),
      ),
    enabled: d.accountId !== '',
  });
  const save = useMutation({
    mutationFn: () =>
      existing
        ? unwrap(
            api.patch<Automation>(
              `/portal/automations/${existing.id}`,
              (({ accountId: _a, ...rest }) => rest)(toBody(d)),
            ),
          )
        : unwrap(api.post<Automation>('/portal/automations', toBody(d))),
    onSuccess: async (created) => {
      await qc.invalidateQueries({ queryKey: ['automations'] });
      toast(existing ? 'Automation updated' : 'Automation created as draft');
      if (!existing && onCreated) onCreated(created);
      onClose();
    },
  });
  const feed = (media.data?.items ?? []).filter((m) =>
    aboutStories(d) ? m.kind === 'story' : m.kind !== 'story',
  );
  const steps = ['Select post or story', 'Automation type', 'Build the automation'];
  return (
    <Modal
      title={wizardTitle(readOnly, existing)}
      onClose={onClose}
      wide
      footer={
        readOnly ? (
          <ReadOnlyFooter step={step} setStep={setStep} onClose={onClose} />
        ) : (
          <>
            {step === 2 && draftProblem(d) ? (
              <div className="alert alert-error" role="alert">
                {draftProblem(d)}
              </div>
            ) : null}
            {step > 0 ? (
              <button type="button" className="btn" onClick={() => setStep(step - 1)}>
                Back
              </button>
            ) : null}
            {step < 2 ? (
              <button type="button" className="btn btn-primary" onClick={() => setStep(step + 1)}>
                Continue
              </button>
            ) : (
              <button
                type="button"
                className="btn btn-primary"
                disabled={draftProblem(d) !== null || save.isPending}
                onClick={() => save.mutate()}
              >
                {existing ? 'Save changes' : 'Create automation'}
              </button>
            )}
          </>
        )
      }
    >
      <fieldset disabled={readOnly} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>
        <div className="steps">
          {steps.map((s, i) => (
            <span key={s} className={i <= step ? 'done' : ''} title={s} />
          ))}
        </div>
        <p className="muted">
          Step {step + 1} of 3: {steps[step]}
        </p>
        {hint ? <div className="alert alert-info">{hint}</div> : null}
        <ErrorAlert error={save.error as ApiError | null} />
        {step === 0 ? (
          <>
            <div className="row">
              <Field label="Account">
                <select
                  className="input"
                  value={d.accountId}
                  onChange={(e) => setD({ ...d, accountId: e.target.value, mediaIds: [] })}
                >
                  {accounts.map((a) => (
                    <option key={a.id} value={a.id}>
                      @{a.username}
                    </option>
                  ))}
                </select>
              </Field>
              {showScope(d) ? <ScopeField d={d} setD={setD} /> : null}
            </div>
            {bindsMedia(d) ? (
              <label className="checkbox">
                <input
                  type="checkbox"
                  checked={d.anyMedia}
                  onChange={(e) => setD({ ...d, anyMedia: e.target.checked })}
                />{' '}
                {aboutStories(d)
                  ? 'Apply to every story on this account'
                  : 'Apply to every post and reel on this account'}
              </label>
            ) : null}
            {!d.anyMedia && bindsMedia(d) ? (
              <div className="grid grid-3" style={{ marginTop: 10 }}>
                {feed.map((m) => (
                  <label
                    key={m.id}
                    className={`card ${d.mediaIds.includes(m.externalId) ? 'plan-card featured' : ''}`}
                    style={{ padding: 10, cursor: 'pointer' }}
                  >
                    <input
                      type="checkbox"
                      className="pill-input"
                      checked={d.mediaIds.includes(m.externalId)}
                      onChange={() =>
                        setD({
                          ...d,
                          mediaIds: d.mediaIds.includes(m.externalId)
                            ? d.mediaIds.filter((x) => x !== m.externalId)
                            : [...d.mediaIds, m.externalId],
                        })
                      }
                    />
                    <div className="card-title">
                      {m.kind} · {m.externalId}
                    </div>
                    <div className="truncate">{m.caption ?? 'no caption'}</div>
                  </label>
                ))}
                {feed.length === 0 ? (
                  <Empty>
                    No media synced for this account. Use Refresh on the Social Accounts page, then
                    come back.
                  </Empty>
                ) : null}
              </div>
            ) : null}
          </>
        ) : null}
        {step === 1 ? (
          <div className="grid grid-2">
            {(
              [
                [
                  'comment',
                  'Comment to DM',
                  'A specific comment made by a user triggers a DM to that user.',
                ],
                ['all_comments', 'All comments to DM', 'Any comment on the post triggers a DM.'],
                [
                  'keyword',
                  'Keyword comment to DM',
                  'A comment containing a keyword triggers a DM.',
                ],
                [
                  'dm',
                  'DM keyword automation',
                  'A DM containing a keyword triggers an automated reply.',
                ],
                ['story', 'Story reply automation', 'A reply to your story triggers a DM.'],
                [
                  'story_mention',
                  'Story mention automation',
                  'Someone mentioning you in their story triggers a DM.',
                ],
                [
                  'post_share',
                  'Post shared to DM',
                  'Someone sharing your post or reel into a DM triggers a reply.',
                ],
                ['flow', 'DM flow', 'Start one of your flows for the user.'],
              ] as const
            ).map(([t, title, text]) => (
              <label
                key={t}
                className={`card ${d.type === t ? 'plan-card featured' : ''}`}
                style={{ cursor: 'pointer' }}
              >
                <input
                  type="radio"
                  className="pill-input"
                  name="type"
                  checked={d.type === t}
                  onChange={() =>
                    setD({
                      ...d,
                      type: t,
                      matcherKind: cardDefaultMatcher(t),
                    })
                  }
                />
                <strong>{title}</strong>
                <div className="muted small">{text}</div>
              </label>
            ))}
          </div>
        ) : null}
        {step === 2 ? (
          <>
            <Field label="Automation name">
              <input
                className="input"
                value={d.name}
                onChange={(e) => setD({ ...d, name: e.target.value })}
              />
            </Field>
            {keywordCard(d) ? (
              <div className="row">
                <Field label="Trigger">
                  <select
                    className="input"
                    value={d.matcherKind}
                    onChange={(e) => setD({ ...d, matcherKind: e.target.value as Matcher['kind'] })}
                  >
                    {/* UI-65: a DM automation may fire on any message too */}
                    <option value="any">{anyLabel(d)}</option>
                    <option value="contains">Contains keyword</option>
                    <option value="exact">Exact match</option>
                    <option value="any_of">Contains any word (multi-keyword)</option>
                    <option value="all_of">Contains all words (multi-keyword)</option>
                  </select>
                </Field>
                {d.matcherKind !== 'any' ? (
                  <Field label="Keywords (comma separated)">
                    <input
                      className="input"
                      value={d.keywords}
                      onChange={(e) => setD({ ...d, keywords: e.target.value })}
                    />
                  </Field>
                ) : null}
              </div>
            ) : null}
            {d.type === 'flow' ? (
              <Field label="Flow">
                <select
                  className="input"
                  value={d.flowId}
                  onChange={(e) => {
                    const chosen = flows.find((f) => f.id === e.target.value);
                    // FLW-30: a template's entry keyword prefills an empty Keywords field
                    const prefill =
                      chosen?.entryKeyword && d.keywords.trim() === ''
                        ? { keywords: chosen.entryKeyword, matcherKind: 'contains' as const }
                        : {};
                    setD({
                      ...d,
                      ...prefill,
                      flowId: e.target.value,
                      flowTrigger: chosen ? flowTriggerOf(chosen) : 'comment',
                      mediaIds: [],
                    });
                  }}
                >
                  <option value="">Select a flow</option>
                  {flows.map((f) => (
                    <option key={f.id} value={f.id}>
                      {f.name} ({f.status}, {typeLabel(flowTriggerOf(f)).toLowerCase()} trigger)
                    </option>
                  ))}
                </select>
              </Field>
            ) : null}
            {aboutPosts(d) ? (
              <>
                <ListEditor
                  label="Reply to comment (round-robin)"
                  values={d.commentReplies}
                  onChange={(v) => setD({ ...d, commentReplies: v })}
                  help={`Variables: ${VARIABLES.join(' ')} · {{tracking_link}} is the tracked copy of your first button URL (empty without a button)`}
                />
                <label className="checkbox">
                  <input
                    type="checkbox"
                    checked={d.aiCommentReply}
                    onChange={(e) => setD({ ...d, aiCommentReply: e.target.checked })}
                  />{' '}
                  Include AI comment reply (uses the AI configuration in Settings; templates become
                  the fallback)
                </label>
                <ListEditor
                  label="Reply to non-followers (round-robin)"
                  values={d.nonFollowerReplies}
                  onChange={(v) => setD({ ...d, nonFollowerReplies: v })}
                />
              </>
            ) : null}
            {/* UI-59: a welcome goes out for every trigger kind (AUT-15), not only comments */}
            {d.type === 'flow' ? null : (
              <ListEditor
                label="Welcome message (round-robin, sent first)"
                values={d.welcomeMessages}
                onChange={(v) => setD({ ...d, welcomeMessages: v })}
                help="Optional greeting before the DMs below; variants rotate across users"
              />
            )}
            {d.type !== 'flow' ? (
              <Field
                label={`DM${d.dmMessages.length > 1 ? 's' : ''} to send (${d.dmMessages.length === 1 ? 'single DM' : 'multiple DMs'})`}
                group
              >
                {d.dmMessages.map((m, i) => (
                  <div key={`dm-${i}`} style={{ marginBottom: 8 }}>
                    <MessageEditor
                      value={m}
                      onChange={(v) =>
                        setD({ ...d, dmMessages: d.dmMessages.map((x, j) => (j === i ? v : x)) })
                      }
                    />
                    <button
                      type="button"
                      className="btn btn-sm btn-link"
                      onClick={() =>
                        setD({ ...d, dmMessages: d.dmMessages.filter((_, j) => j !== i) })
                      }
                    >
                      Remove this DM
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  className="btn btn-sm"
                  onClick={() =>
                    setD({ ...d, dmMessages: [...d.dmMessages, { kind: 'text', text: '' }] })
                  }
                >
                  Add another DM
                </button>
              </Field>
            ) : null}
            <div className="row">
              <Field label="Wait / delay before DM">
                <select
                  className="input"
                  value={d.delaySeconds}
                  onChange={(e) => setD({ ...d, delaySeconds: Number(e.target.value) })}
                >
                  <option value={0}>Immediately</option>
                  <option value={10}>10 seconds later</option>
                  <option value={30}>30 seconds later</option>
                  <option value={60}>1 minute later</option>
                </select>
              </Field>
              <Field
                label="Human handoff message"
                help="Sent when a reply cannot be understood (three failed answers in a flow, or an unrecognised DM after this automation)"
              >
                <input
                  className="input"
                  value={d.handoffMessage}
                  onChange={(e) => setD({ ...d, handoffMessage: e.target.value })}
                  placeholder="A specific point of contact will connect you regarding your query"
                />
              </Field>
            </div>
            <div className="row">
              <Field
                label="Active from (optional)"
                help="Schedule: the automation only runs inside this window"
              >
                <input
                  className="input"
                  type="datetime-local"
                  value={d.activeFrom}
                  onChange={(e) => setD({ ...d, activeFrom: e.target.value })}
                />
              </Field>
              <Field label="Active until (optional)">
                <input
                  className="input"
                  type="datetime-local"
                  value={d.activeUntil}
                  onChange={(e) => setD({ ...d, activeUntil: e.target.value })}
                />
              </Field>
            </div>
          </>
        ) : null}
      </fieldset>
    </Modal>
  );
}

/** ANA-13 (revised 3): the API sends null for a figure the plan does not get; never show it as 0. */
function gatedCell(value: number | null | undefined, gated: boolean): ReactNode {
  return value === null || (value === undefined && gated) ? (
    <span className="muted" title="Clicks and conversions are reported from Legend">
      —
    </span>
  ) : (
    (value ?? 0)
  );
}

export function AutomationsPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const { can, me } = useSession();
  // ANA-13 (revised 3): without link analytics a missing stats row is a gated figure, not 0
  const linkGated = me?.features.linkAnalytics === false;
  const accounts = useAccounts();
  const all = useAutomations();
  // UI-64: the media of every account, to name what an automation is bound to
  const mediaIndex = useQuery({
    queryKey: ['all-media', accounts.data?.items.map((a) => a.id)],
    queryFn: async () =>
      new Map(
        (
          await Promise.all(
            (accounts.data?.items ?? []).map((a) =>
              unwrap(api.get<{ items: Media[] }>(`/portal/social-accounts/${a.id}/media`)).then(
                (r) => r.items,
              ),
            ),
          )
        )
          .flat()
          .map((m) => [m.externalId, m] as const),
      ),
    enabled: (accounts.data?.items.length ?? 0) > 0,
  });
  const kindOf = (externalId: string) => mediaIndex.data?.get(externalId)?.kind;
  // CON-10: /app/automations?flow=<id> shows the automations attached to that flow
  const [params] = useSearchParams();
  const flowFilter = params.get('flow');
  const list = {
    ...all,
    data: all.data
      ? { items: all.data.items.filter((a) => flowFilter === null || a.flowId === flowFilter) }
      : all.data,
  };
  const stats = useQuery({
    queryKey: ['automation-stats'],
    queryFn: () => unwrap(api.get<Items<AutomationStats>>('/portal/analytics/automations')),
  });
  const flows = useQuery({
    queryKey: ['flows'],
    queryFn: () => unwrap(api.get<Items<Flow>>('/portal/flows')),
  });
  const [editing, setEditing] = useState<{
    draft: Draft;
    existing: Automation | null;
    readOnly?: boolean;
  } | null>(null);
  const statsById = useMemo(
    () => new Map((stats.data?.items ?? []).map((s) => [s.automationId, s])),
    [stats.data],
  );
  const accountById = useMemo(
    () => new Map((accounts.data?.items ?? []).map((a) => [a.id, a])),
    [accounts.data],
  );
  const useAction = (verb: string) =>
    useMutation({
      mutationFn: (id: string) => unwrap(api.post(`/portal/automations/${id}/${verb}`)),
      onSuccess: async () => {
        await qc.invalidateQueries({ queryKey: ['automations'] });
        toast(`Automation ${verb}d`.replace('dd', 'd'));
      },
      onError: (e: ApiError) => toast(e.message, 'error'),
    });
  const publish = useAction('publish');
  const pause = useAction('pause');
  const resume = useAction('resume');
  const duplicate = useAction('duplicate');
  const del = useMutation({
    mutationFn: (id: string) => unwrap(api.del(`/portal/automations/${id}`)),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['automations'] }),
    onError: (e: ApiError) => toast(e.message, 'error'),
  });
  const describeTrigger = triggerLabel;
  return (
    <>
      <PageHeader
        title="Automations"
        subtitle="Rules that turn comments, story replies and DMs into conversations"
        actions={
          can('automation:create') ? (
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => setEditing({ draft: empty, existing: null })}
              disabled={(accounts.data?.items.length ?? 0) === 0}
            >
              New automation
            </button>
          ) : null
        }
      />
      {flowFilter ? (
        <div className="alert alert-info">
          Showing automations that use flow{' '}
          <strong>{flows.data?.items.find((f) => f.id === flowFilter)?.name ?? flowFilter}</strong>.{' '}
          <Link to="/app/automations">Show all</Link>
        </div>
      ) : null}
      <ErrorAlert error={list.error} />
      <DataTable>
        <table className="table">
          <thead>
            <tr>
              <th>Automation</th>
              <th>Type</th>
              <th>Account</th>
              <th>Trigger</th>
              <th>Status</th>
              <th>Linked media</th>
              <th>Last trigger</th>
              <th>Users</th>
              <th>Sent</th>
              <th>Clicks</th>
              <th>Conversions</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {(list.data?.items ?? []).map((a) => {
              const s = statsById.get(a.id);
              return (
                <tr key={a.id}>
                  <td>
                    <strong>{a.name}</strong>
                    {a.flowId ? (
                      <div className="small muted">
                        flow: <Link to={`/app/flows/${a.flowId}`}>open</Link>
                      </div>
                    ) : null}
                  </td>
                  <td>{mediaTypeLabel(a, kindOf)}</td>
                  <td>@{accountById.get(a.accountId)?.username ?? '…'}</td>
                  <td>{describeTrigger(a)}</td>
                  <td>
                    <Badge value={a.status} />
                  </td>
                  <td className="truncate">
                    {a.mediaBinding.kind === 'any'
                      ? a.triggerType === 'story_reply'
                        ? 'All stories'
                        : 'All media'
                      : a.mediaBinding.mediaIds.map((id, i) => (
                          <span key={id} title={id}>
                            {i > 0 ? ', ' : ''}
                            {mediaLabel(mediaIndex.data?.get(id)?.caption, id)}
                          </span>
                        ))}
                  </td>
                  <td>{relTime(s?.lastTriggeredAt)}</td>
                  <td>{s?.usersTriggered ?? 0}</td>
                  <td>{s?.messagesSent ?? 0}</td>
                  <td>{gatedCell(s?.clicks, linkGated)}</td>
                  <td>{gatedCell(s?.conversions, linkGated)}</td>
                  <td>{fmtDate(a.createdAt)}</td>
                  <td className="actions">
                    {can('automation:update') ? (
                      <button
                        type="button"
                        className="btn btn-sm"
                        onClick={() => setEditing({ draft: toDraft(a), existing: a })}
                      >
                        Edit
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="btn btn-sm"
                        onClick={() =>
                          setEditing({ draft: toDraft(a), existing: a, readOnly: true })
                        }
                      >
                        View
                      </button>
                    )}{' '}
                    {a.status !== 'active' && can('automation:publish') ? (
                      <button
                        type="button"
                        className="btn btn-sm btn-primary"
                        onClick={() => publish.mutate(a.id)}
                      >
                        Publish
                      </button>
                    ) : null}{' '}
                    {a.status === 'active' && can('automation:pause') ? (
                      <button
                        type="button"
                        className="btn btn-sm"
                        onClick={() => pause.mutate(a.id)}
                      >
                        Pause
                      </button>
                    ) : null}{' '}
                    {a.status === 'paused' && can('automation:pause') ? (
                      <button
                        type="button"
                        className="btn btn-sm"
                        onClick={() => resume.mutate(a.id)}
                      >
                        Resume
                      </button>
                    ) : null}{' '}
                    {can('automation:create') ? (
                      <button
                        type="button"
                        className="btn btn-sm"
                        onClick={() => duplicate.mutate(a.id)}
                      >
                        Duplicate
                      </button>
                    ) : null}{' '}
                    {can('automation:delete') && a.status !== 'active' ? (
                      <button
                        type="button"
                        className="btn btn-sm btn-danger"
                        onClick={() => del.mutate(a.id)}
                      >
                        Delete
                      </button>
                    ) : null}
                  </td>
                </tr>
              );
            })}
            {list.data?.items.length === 0 ? (
              <tr>
                <td colSpan={13}>
                  <Empty>No automations yet. Create one to start turning comments into DMs.</Empty>
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </DataTable>
      {editing ? (
        <AutomationWizard
          initial={editing.draft}
          existing={editing.existing}
          readOnly={editing.readOnly ?? false}
          onClose={() => setEditing(null)}
          accounts={accounts.data?.items ?? []}
          flows={flows.data?.items ?? []}
        />
      ) : null}
      <Card title="Variables you can use in any message">
        <div className="pill-row">
          {VARIABLES.map((v) => (
            <code key={v} className="badge">
              {v}
            </code>
          ))}
        </div>
      </Card>
    </>
  );
}
